import { getToken } from '@verantos-dev/core-auth';
import { getBaseApiUrl } from '@verantos-dev/core-constants';

import type { MappingResponse, MappingResult, MappingSummary } from '../types';

/**
 * S3 File Structure (from backend)
 *
 * The backend stores mapping results in S3 with this structure:
 * {
 *   "codes": [...],
 *   "mappings": [ { code: "...", type: "Source", ...mappedRows: [...] }, ... ],
 *   "mapping_summary": {
 *     "totalCodes": number,
 *     "mappedCount": number,
 *     "unmappedCount": number,
 *     "notFoundCount": number,
 *     "didntNeedToMapCount": number
 *   },
 *   "validation": {...}
 * }
 */

interface S3MappingResponse {
  file_name: string;
  codes?: string[];
  mappings?: MappingResult[];
  mapping_summary?: {
    totalCodes?: number;
    mappedCount?: number;
    unmappedCount?: number;
    notFoundCount?: number;
    alreadyStandardCount?: number;
  };
  validation?: Record<string, unknown>;
  total_codes?: number;
}

/**
 * Transform S3 API response to frontend MappingResponse format
 *
 * The backend returns mappings with conceptClass field that needs to be
 * renamed to class for the frontend format.
 *
 * This function normalizes the format for the UI.
 */
export function transformS3ResponseToMappingResponse(s3Data: S3MappingResponse): MappingResponse {
  const mappings = s3Data.mappings || [];
  const summary = s3Data.mapping_summary || {};

  // Mappings is already an array
  const results: MappingResult[] = mappings.map((mapping) => {
    // Recursively transform mapped rows if they exist
    const transformedMappedRows = mapping.mappedRows
      ? mapping.mappedRows.map((row) => ({
          ...row,
          // Rename conceptClass to class for frontend
          class: (row as any).conceptClass,
          isCollapsible: row.isCollapsible ?? false,
        }))
      : undefined;

    return {
      type: mapping.type,
      id: mapping.id,
      name: mapping.name,
      code: mapping.code,
      vocabulary: mapping.vocabulary,
      standard: mapping.standard,
      domain: mapping.domain,
      class: (mapping as any).conceptClass,
      status: mapping.status,
      mappedCount: mapping.mappedCount,
      reasoning: mapping.reasoning,
      isCollapsible: mapping.isCollapsible ?? false,
      mappedRows: transformedMappedRows,
    };
  });

  // Build summary with default values if missing
  const mappingSummary: MappingSummary = {
    totalCodes: summary.totalCodes ?? results.length,
    mappedCount: summary.mappedCount ?? 0,
    unmappedCount: summary.unmappedCount ?? 0,
    notFoundCount: summary.notFoundCount ?? 0,
    alreadyStandardCount: summary.alreadyStandardCount ?? 0,
  };

  return {
    fileName: s3Data.file_name,
    results,
    summary: mappingSummary,
  };
}

/**
 * Fetch mapping results from message-gateway S3 endpoint
 *
 * Requires authentication via cookies/session.
 *
 * @param sessionId - Unique session ID from the OMOP mapping agent
 * @param fileName - File name (without extension) from the mapping session
 * @param originalFileName - Optional original filename to display in the UI
 * @returns Transformed MappingResponse ready for the UI
 *
 * @throws Error if the API call fails or session credentials are invalid
 */
export async function fetchMappingResultsFromS3(
  sessionId: string,
  fileName: string,
  originalFileName?: string
): Promise<MappingResponse> {
  if (!sessionId || !fileName) {
    throw new Error('sessionId and fileName are required to fetch mapping results');
  }

  // Remove .json extension if present (backend will add it)
  const fileNameWithoutExtension = fileName.replace(/\.json$/, '');

  // URL encode the session_id and fileName (handles special characters like |)
  const encodedSessionId = encodeURIComponent(sessionId);
  const encodedFileName = encodeURIComponent(fileNameWithoutExtension);

  // Use the default base API URL from environment
  const baseUrl = getBaseApiUrl();
  const url = `${baseUrl}/v1/message-gateway/codes/${encodedSessionId}/${encodedFileName}`;

  try {
    // Get authentication token (same as baseApiV2)
    const token = await getToken();

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.detail || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(`Failed to fetch mapping results: ${errorMessage}`);
    }

    const data: S3MappingResponse = await response.json();
    const mappingResponse = transformS3ResponseToMappingResponse(data);

    // Use original filename if provided, otherwise use the response filename
    if (originalFileName) {
      mappingResponse.fileName = originalFileName;
    }

    return mappingResponse;
  } catch (error) {
    // Re-throw with context
    if (error instanceof Error) {
      throw new Error(`Error fetching mapping results for session ${sessionId}: ${error.message}`);
    }
    throw error;
  }
}
